/*
 * Settings → Integrations → API keys — the wording the card uses, pure so it can be checked
 * (`node src/lib/apiKeys.checks.mjs`). DESIGN-api-and-webhooks.md step 1, 25/10/2026.
 */

/* The ticks, in the order the server stores them (`services/api_keys.SCOPES`). Each new tick
 * goes last there so older keys read the same: `book` (step 3), then `manage_people` (step 4),
 * then `manage_allowances` (step 5). */
export const SCOPES = ['people', 'leave', 'balances', 'sickness', 'book', 'manage_people', 'manage_allowances'];

/* The window's two groups (step 3, Rob 26/10/2026, mock-up `api-write-mock-v1.html`). */
export const READ_TICKS = ['people', 'leave', 'balances', 'sickness'];
export const CHANGE_TICKS = ['book', 'manage_people', 'manage_allowances'];

export const SCOPE_HINTS = {
  people: 'Names, work emails, job titles, departments, locations',
  leave: 'Approved bookings and who’s off, day by day',
  balances: 'Annual leave this leave year: allowance, taken, booked, left. Time in lieu: what’s owed, and each claim and correction',
  sickness: 'Otherwise sickness shows as “Time off”. Sickness is health data, so only tick this if the system using the key needs it.',
  book: 'Book leave for anyone, approve or decline requests, cancel leave and give back days, just as you can in the app. Everything it does is under your name, marked “via API key”.',
  manage_people: 'Add, edit, archive and restore people, and add, edit and delete empty departments. It can’t touch admins or change anyone’s role.',
  manage_allowances: 'Set a person’s yearly allowance, record and correct time in lieu, approve or decline time in lieu claims, and add and edit leave types. It can’t touch admins, private or sickness leave types, or Annual Leave’s name, and it can’t delete a leave type.',
};

/* The warning under "Book and approve leave" once it's ticked. */
export const BOOK_WARNING = {
  strong: 'This key can change anybody’s leave, as you.',
  rest: 'If you leave or stop being an admin, it stops working. Treat it like your password.',
};

/* The warning under "Manage people and departments" once it's ticked (step 4, Rob chose layout
 * A of `Claude outputs/api-people-mock-v1.html`: each change tick has its own warning). */
export const MANAGE_WARNING = {
  strong: 'This key can add people, and each one adds a person to your bill.',
  rest: 'It can also archive anyone except admins, as you. Archiving doesn’t take them off the bill; only deleting someone in the app does.',
};

/* The warning under "Manage allowances and leave types" once it's ticked (step 5, Rob 26/10/2026,
 * mock-up `Claude outputs/api-allowances-mock-v1.html`). */
export const ALLOWANCES_WARNING = {
  strong: 'This key can change how much leave anybody has, as you.',
  rest: 'It can also add and change leave types. If you leave or stop being an admin, it stops working.',
};

/* Each change tick's warning, shown right under it once ticked. */
export const CHANGE_WARNINGS = { book: BOOK_WARNING, manage_people: MANAGE_WARNING, manage_allowances: ALLOWANCES_WARNING };

/* At least one thing it can read or change — sickness alone does nothing (the server says the
 * same). */
export function canMake(name, scopes) {
  return !!(name || '').trim() && (scopes || []).some((s) => s !== 'sickness' && SCOPES.includes(s));
}

const pad = (n) => String(n).padStart(2, '0');

/* "used 3 minutes ago" / "never used". UK date beyond a week (CONVENTIONS.md). */
export function usedWords(iso, nowMs = Date.now()) {
  const at = Date.parse(iso || '');
  if (!Number.isFinite(at)) return 'never used';
  const mins = Math.floor((nowMs - at) / 60000);
  if (mins < 2) return 'used just now';
  if (mins < 60) return `used ${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours === 1 ? 'used 1 hour ago' : `used ${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? 'used yesterday' : `used ${days} days ago`;
  const d = new Date(at);
  return `last used ${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}
